import React from "react";
import { Document, Page, Path, StyleSheet, Svg, Text, View } from "@react-pdf/renderer";
import { commonStyles, formatPhoneNumber } from "../../Services/ReactPDFService.jsx";

// Simple Blush Cover Letter Template - Matching the Simple Blush Resume Design
const styles = StyleSheet.create({
  ...commonStyles,
  page: {
    ...commonStyles.page,
    fontFamily: "Roboto",
    backgroundColor: "#ffffff",
    padding: 0,
  },
  // Header with white background (matching resume)
  headerContainer: {
    backgroundColor: "#ffffff",
    paddingHorizontal: 40,
    paddingVertical: 24,
    borderBottomWidth: 2,
    borderBottomColor: "#e8b4b8",
  },
  name: {
    fontSize: 28,
    fontWeight: 700,
    color: "#2d3748",
    marginBottom: 2,
    letterSpacing: 0.5,
    lineHeight: 1.2,
  },
  contactRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 12,
    marginTop: 8,
  },
  contactItem: {
    flexDirection: "row",
    alignItems: "center",
  },
  contactIcon: {
    width: 9,
    height: 9,
    marginRight: 4,
  },
  contactText: {
    fontSize: 9,
    color: "#718096",
    lineHeight: 1,
  },
  // Main content area
  contentWrapper: {
    padding: "35 45",
  },
  // Body text styles
  bodyText: {
    fontSize: 11,
    lineHeight: 1.8,
    color: "#4a5568",
    textAlign: "justify",
  },
  // Footer accent line
  footerAccent: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: 6,
    backgroundColor: "#e8b4b8",
  },
});

const SimpleBlushCoverLetter = ({ coverLetterData }) => {
  const { header, generatedContent } = coverLetterData || {};
  const { name, email, phone, location } = header || {};

  // Parse content into paragraphs
  const paragraphs = generatedContent
    ? generatedContent.split("\n\n").filter((p) => p.trim())
    : ["No content generated yet."];

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header with white background and blush border */}
        <View style={styles.headerContainer}>
          <Text style={styles.name}>{name || "[Your Name]"}</Text>
          <View style={styles.contactRow}>
            {location && (
              <View style={styles.contactItem}>
                <Svg style={styles.contactIcon} viewBox="0 0 24 24">
                  <Path
                    d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"
                    fill="#e8b4b8"
                  />
                </Svg>
                <Text style={styles.contactText}>{location}</Text>
              </View>
            )}
            {email && (
              <View style={styles.contactItem}>
                <Svg style={styles.contactIcon} viewBox="0 0 24 24">
                  <Path
                    d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"
                    fill="#e8b4b8"
                  />
                </Svg>
                <Text style={styles.contactText}>{email}</Text>
              </View>
            )}
            {phone && (
              <View style={styles.contactItem}>
                <Svg style={styles.contactIcon} viewBox="0 0 24 24">
                  <Path
                    d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"
                    fill="#e8b4b8"
                  />
                </Svg>
                <Text style={styles.contactText}>{formatPhoneNumber(phone)}</Text>
              </View>
            )}
          </View>
        </View>

        {/* Main Content */}
        <View style={styles.contentWrapper}>
          {/* Body Content - all paragraphs with same style */}
          {paragraphs.map((paragraph, index) => (
            <View key={index} style={{ marginBottom: 14 }}>
              <Text style={styles.bodyText}>{paragraph.trim()}</Text>
            </View>
          ))}
        </View>

        {/* Footer accent line */}
        <View style={styles.footerAccent} />
      </Page>
    </Document>
  );
};

export default SimpleBlushCoverLetter;
